import functions from './functions'
import packages from './packages'

class SubscriptWildcardWrapper {
  constructor (array) {
    this.array = array
  }
}

class ListWrapper {
  array

  constructor (array) { this.array = array }

  groupBy (by) {
      return this.array.reduce(function (carry, item) {
        const value = typeof by === 'function' ? by(item) : item[by];
        (carry[value] = carry[value] || []).push(item)
        return carry
      }, {})
  }
}

class ObjectWrapper {
  object

  constructor (object) {
    this.object = object
  }

  entries () {
    return Object.entries(this.object)
  }
}

export default class Resolve {
  symbolTable = {}

  constructor (symbolTable = {}, functionTable = {}, context = null) {
    this.functionTable = Object.assign({}, functions, functionTable)
    this.symbolTable = Object.assign({}, packages, symbolTable)
    this.constants = {
      true: true,
      false: false,
      null: null,
    }
    this.context = context
  }

  unary (op, x) {
    if (op === '!') return !x
    if (op === '-') return -x
    return x
  }

  conditional (condition, x, y) {
    return condition ? x(this) : y(this)
  }

  relational (x, op, y) {
    if (op === '<') return x < y
    if (op === '>') return x > y
    if (op === '<=') return x <= y
    if (op === '>=') return x >= y
  }

  equality (x, op, y) {
    if (op === '==') return x === y
    if (op === '!=') return x !== y
  }

  and (x, y) {
    return !!x && !!y
  }

  or (x, y) {
    return !!x || !!y
  }

  bitwiseAnd (x, y) {
    return x & y
  }

  bitwiseOr (x, y) {
    return x | y
  }

  bitwiseXor (x, y) {
    return x ^ y
  }

  concat (x, y) {
    return x + y
  }

  add (x, y) {
    if (this.hasMethod(x, '__add__')) {
      return x.__add__(y)
    }
    if (this.hasMethod(y, '__add__')) {
      return y.__add__(x)
    }
    return x + y
  }

  sub (x, y) {
    if (this.hasMethod(x, '__sub__')) {
      return x.__sub__(y)
    }
    return x - y
  }

  mult (x, y) {
    if (this.hasMethod(x, '__mult__')) {
      return x.__mult__(y)
    }
    if (this.hasMethod(y, '__mult__')) {
      return y.__mult__(x)
    }
    return x * y
  }

  div (x, y) {
    if (this.hasMethod(x, '__div__')) {
      return x.__div__(y)
    }
    return x / y
  }

  mod (x, y) {
    if (this.hasMethod(x, '__mod__')) {
      return x.__mod__(y)
    }
    return x % y
  }

  shift (x, op, y) {
    return op === '>>' ? x >> y : x << y
  }

  arrow (args, expression) {
    // TODO: scope args so that they do not overwrite existing symbols, efficiently.
    const resolve = this
    return function () {
      args.forEach((arg, index) => {
        resolve.symbolTable[arg] = arguments[index]
      })
      return expression(resolve)
    }
  }

  call (identifier, args) {
    if (identifier in this.functionTable) {
      return this.functionTable[identifier](...args)
    }
    return null
  }

  method (obj, name, args) {
    if (obj && '__call' in obj) {
      // Use "magic method" of obj
      return obj.__call(name, args)
    }
    if (Array.isArray(obj) && name in ListWrapper.prototype) {
      const wrapper = new ListWrapper(obj)
      return wrapper[name].apply(wrapper, args)
    }
    if (obj && typeof obj === 'object' && name in ObjectWrapper.prototype) {
      const wrapper = new ObjectWrapper(obj)
      return wrapper[name].apply(wrapper, args)
    }
    return obj && (name in obj) ? obj[name].apply(obj, args) : null
  }

  subscript (obj, index) {
    return obj[index]
  }

  subscriptWildcard (obj) {
    return new SubscriptWildcardWrapper(obj)
  }

  member (obj, name) {
    if (obj && typeof obj.__get === 'function') {
      // Use "magic method" of obj
      return obj.__get(name)
    }
    if (obj instanceof SubscriptWildcardWrapper) {
      // return an array consisting of obj[*].member
      return (obj.array || []).map(item => this.member(item, name))
    }
    return obj ? obj[name] : null
  }

  symbol (identifier) {
    if (identifier in this.constants) {
      return this.constants[identifier]
    }
    return this.symbolTable[identifier]
  }

  dictionary (pairs) {
    return Object.fromEntries(pairs)
  }

  hasMethod (obj, method) {
    return obj && typeof obj === 'object' && method in obj
  }
}
